import { styled } from '@linaria/react';
import { colors, fonts, layout, semantic, space } from '../../theme/tokens';

const CARD_RADIUS = '24px';
const MIN_HEIGHT = '340px';

export const EmptyCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space[5]};
  min-height: ${MIN_HEIGHT};
  padding: ${space[5]} ${space[7]};
  background: ${semantic.bg.surfaceSubtle};
  border-radius: ${CARD_RADIUS};
  text-align: center;
`;

export const EmptyHeading = styled.p`
  margin: 0;
  font-family: ${fonts.heading};
  font-size: ${fonts.size.xl};
  font-weight: ${fonts.weight.normal};
  line-height: 1.2;
  color: ${semantic.text.primary};
`;

export const PopulatedCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${space[5]};
  padding: ${space[5]};
  background: ${colors.white};
  border: 1px solid ${semantic.border.subtle};
  border-radius: ${CARD_RADIUS};
  min-height: ${MIN_HEIGHT};

  @media (min-width: ${layout.desktopBreakpoint}) {
    flex-direction: row;
    align-items: stretch;
    gap: ${space[6]};
    padding: ${space[6]};
  }
`;

export const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 16px;
  background: ${semantic.bg.surfaceSubtle};
  flex-shrink: 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    width: 40%;
    max-width: 360px;
    aspect-ratio: auto;
    align-self: stretch;
  }
`;

export const Image = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ImagePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semantic.text.secondary};
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  flex: 1;
  min-width: 0;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FieldLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  line-height: 1.4;
  color: ${semantic.text.secondary};
`;

export const FieldValue = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.medium};
  line-height: 1.4;
  color: ${semantic.text.primary};
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-end;
  padding: ${space[1]} ${space[3]};
  border-radius: ${layout.borderRadiusFull};
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.medium};
  line-height: 1.4;

  &[data-status='active'] {
    background: ${semantic.bg.positiveSubtle};
    color: ${semantic.text.positive};
  }

  &[data-status='cancelled'] {
    background: ${semantic.bg.dangerSubtle};
    color: ${semantic.text.danger};
  }

  &[data-status='complete'] {
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
  }
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: ${space[3]};
`;

export const LoadingPillSlot = styled.div`
  align-self: flex-end;
`;

export const ListCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  padding: ${space[4]};
  background: ${colors.white};
  border: 1px solid ${semantic.border.subtle};
  border-radius: ${layout.borderRadiusLg};
`;

export const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${space[2]};
`;

export const ImageTile = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: ${layout.borderRadiusMd};
  background: ${semantic.bg.surfaceSubtle};
`;

export const TileImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const TilePlaceholder = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semantic.text.secondary};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space[3]};
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ListStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${space[1]} ${space[3]};
  border-radius: ${layout.borderRadiusFull};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};
  white-space: nowrap;

  &[data-status='active'] {
    background: ${semantic.bg.positiveSubtle};
    color: ${semantic.text.positive};
  }

  &[data-status='cancelled'] {
    background: ${semantic.bg.dangerSubtle};
    color: ${semantic.text.danger};
  }

  &[data-status='complete'] {
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
  }
`;

export const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${space[3]};
`;

export const MetaField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  &:last-child {
    align-items: flex-end;
    text-align: right;
  }
`;

export const ListFieldLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const ListFieldValue = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ListActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  margin-top: ${space[1]};

  & > button {
    width: 100%;
  }
`;
